import { useEffect, useRef, useState, type ReactNode } from 'react'
import { fg } from '../copyFeatGrowth'
import type { Card } from '../lib/browse'
import { useBrowsePlan } from '../lib/browse'
import { comeUrl, createLink, forgetLink, inCount, linksFor } from '../lib/comeWith'
import { qaPreset, useFlag, useQa } from '../lib/flags'
import { calendarAddCount, markPlusSeen, plusDue } from '../lib/plus'
import { shareOrCopy } from '../lib/share'
import { People } from '../ui/icons'
import { Spinner } from '../ui/Spinner'
import { buttonClass } from '../ui/styles'
import { InstallHint } from './InstallHint'
import { PlusOffer } from './PlusOffer'

/**
 * Growth pieces on a listing's detail (features 10/16), around the calendar buttons (DetailCalendar is another lane's
 * file, so a tap on it is noticed here by a capture listener, not by changing it):
 * - after a first calendar add or a save: the one-time Add to home screen hint (flag install_hint);
 * - after the 3rd calendar add: the soft Plus card, at most once a week (flag plus_offer, OFF in production);
 * - "Come with me?": a link for this one pick, shared from the person's own phone (flag come_with).
 */
export function DetailGrowth({ card, calendar }: { card: Card; calendar: ReactNode }) {
  const installOn = useFlag('install_hint')
  const plusOn = useFlag('plus_offer')
  const comeOn = useFlag('come_with')
  const qa = useQa()
  const { added } = useBrowsePlan()
  const saved = added[card.item.id]?.status === 'added'
  const [adds, setAdds] = useState<number | null>(null)
  const [plusShown, setPlusShown] = useState(false)
  const checked = useRef(false)
  const [now] = useState(Date.now)
  const endIso = card.item.end_at ?? card.item.start_at
  const over = card.item.kind === 'event' && !!endIso && new Date(endIso).getTime() < now

  // one count per detail open (only when a feature needs it), and again a moment after a calendar button is tapped
  const recount = () => { void calendarAddCount().then(setAdds).catch(() => {}) }
  useEffect(() => { if (installOn || plusOn) recount() }, [card.item.id, installOn, plusOn, qa.preset, qa.adds]) // eslint-disable-line react-hooks/exhaustive-deps
  const tapped = (e: React.MouseEvent) => {
    const t = (e.target as HTMLElement).closest('[data-review-id="cal-apple"],[data-review-id="cal-google"]')
    if (t && (installOn || plusOn)) window.setTimeout(recount, 1500)
  }
  useEffect(() => { setPlusShown(false); checked.current = false }, [qa.preset])
  const plus = plusOn && adds !== null && (plusShown || plusDue(adds))
  useEffect(() => {
    if (plus && !plusShown && !checked.current) { checked.current = true; setPlusShown(true); if (qaPreset() === 'none') markPlusSeen() }
  }, [plus, plusShown])
  const install = installOn && !plus && ((adds ?? 0) >= 1 || saved)

  return (
    <>
      <div onClickCapture={tapped}>{calendar}</div>
      {install && <InstallHint />}
      {plus && <PlusOffer surface="after_add" />}
      {comeOn && !over && <ComeWithShare card={card} />}
    </>
  )
}

/** "Come with me?": make a link for this pick and hand it to the share sheet (or copy it); then say when a friend is in. */
function ComeWithShare({ card }: { card: Card }) {
  const id = card.item.id
  const [busy, setBusy] = useState(false)
  const [state, setState] = useState<null | 'shared' | 'copied' | 'error' | { manual: string }>(null)
  const [count, setCount] = useState<number | null>(null)
  const qa = useQa()
  // QA preset "a pending Come with me invite": this detail reads as if a link went out and nobody answered yet
  const has = linksFor(id).length > 0 || qa.preset === 'come_pending'
  useEffect(() => {
    if (!linksFor(id).length) return
    let live = true
    void inCount(id).then((n) => { if (live) setCount(n) }).catch(() => {})
    return () => { live = false }
  }, [id])

  const going = useRef(false) // a double tap makes one link, not two
  const go = async () => {
    if (going.current) return
    going.current = true
    setBusy(true)
    setState(null)
    try {
      const link = await createLink(id)
      const url = comeUrl(link.token)
      const r = await shareOrCopy(url, fg('fg.come_share_title'))
      // a cancelled share sent nothing: forget the link so the detail does not wait on it
      if (r === 'cancelled') forgetLink(link.token)
      setState(r === 'manual' ? { manual: url } : r === 'cancelled' ? null : r)
      if (count === null && r !== 'cancelled') setCount(0)
    } catch {
      setState('error')
    } finally {
      setBusy(false)
      going.current = false
    }
  }
  const status = count && count > 0 && qa.preset !== 'come_pending' ? (count === 1 ? fg('fg.come_in_one') : fg('fg.come_in_many', { count })) : (has || state === 'shared' || state === 'copied') ? fg('fg.come_waiting') : null
  return (
    <section data-review-id="come-with" className="mt-4 border-t border-line pt-3">
      <p className="t-meta text-ink-2">{fg('fg.come_help')}</p>
      <button type="button" data-review-id="come-with-cta" onClick={() => void go()} disabled={busy} className={buttonClass('secondary', 'md', 'mt-2 w-full')}>
        {busy ? <Spinner size={16} label={false} className="text-current" /> : <People size={18} />}{busy ? fg('fg.come_making') : fg('fg.come_cta')}
      </button>
      <div aria-live="polite">
        {state === 'shared' && <p role="status" className="t-meta mt-2 text-ink">{fg('fg.come_shared')}</p>}
        {state === 'copied' && <p role="status" className="t-meta mt-2 text-ink">{fg('fg.come_copied')}</p>}
        {state === 'error' && <p role="alert" className="t-meta mt-2 text-error">{fg('fg.come_error')}</p>}
        {state && typeof state === 'object' && (
          <div className="mt-2">
            <p className="t-meta text-ink">{fg('fg.come_manual')}</p>
            <input readOnly value={state.manual} onFocus={(e) => e.currentTarget.select()} className="t-meta mt-1 w-full border border-line bg-bg px-2 py-2 text-ink" aria-label={fg('fg.come_manual')} />
          </div>
        )}
        {status && <p data-review-id="come-with-status" className="t-meta mt-2 font-semibold text-ink">{status}</p>}
      </div>
    </section>
  )
}
